import { atom, selector } from "recoil";

import type { ModalBulkFindReplaceState } from "Pages/RecipientsList/model";

/* Modal find & replace */

export const modalBulkFindReplaceState = atom<ModalBulkFindReplaceState>({
  key: "recipientsList_modalBulkFindReplaceState",
  default: {
    show: false,
    FindColumn: undefined,
    FindText: "",
    ReplaceText: "",
    FindCaseSensitive: false,
    FindType: "Contains",

    isFindTextDirty: false,
    isReplaceTextDirty: false,

    previewStatus: "initial",

    previewFindText: "",
    previewFindCaseSensitive: false,
    previewReplaceText: "",
    previewFindType: "Contains",

    idTask: undefined,
    pollingIteration: 0,

    items: [],
    pageNumber: 1,
    pageSize: 5,
    totalItems: 0,

    EmptyField: undefined,
    isEmptyFieldDirty: false,
  },
});

export const modalBulkFindReplaceFindTextValidState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceFindTextValidState",
  get: ({ get }) => {
    const { FindColumn, FindText, FindType } = get(modalBulkFindReplaceState);

    if (
      FindColumn !== undefined &&
      ["Email", "MobilePrefix", "MobileNumber"].includes(FindColumn) &&
      FindType === "Equals" &&
      FindText.length === 0
    ) {
      return false;
    }

    return true;
  },
});

export const modalBulkFindReplaceReplaceTextValidState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceReplaceTextValidState",
  get: ({ get }) => {
    const { FindColumn, ReplaceText, FindType } = get(
      modalBulkFindReplaceState,
    );

    if (
      FindColumn !== undefined &&
      ["Email", "MobilePrefix", "MobileNumber"].includes(FindColumn) &&
      FindType === "Equals" &&
      ReplaceText.length === 0
    ) {
      return false;
    }

    return true;
  },
});

export const modalBulkFindReplaceEqualsDisabledState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceEqualsDisabledState",
  get: ({ get }) => {
    const { FindColumn, FindText, ReplaceText } = get(
      modalBulkFindReplaceState,
    );

    if (
      FindColumn !== undefined &&
      ["Email", "MobilePrefix", "MobileNumber"].includes(FindColumn) &&
      (ReplaceText.length === 0 || FindText.length === 0)
    ) {
      return true;
    }

    return false;
  },
});

export const modalBulkFindReplaceCanPreviewState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceCanPreviewState",
  get: ({ get }) => {
    const { FindColumn, FindText, FindType, ReplaceText, previewStatus } = get(
      modalBulkFindReplaceState,
    );

    const validFindText = get(modalBulkFindReplaceFindTextValidState);
    const validReplaceText = get(modalBulkFindReplaceReplaceTextValidState);

    if (previewStatus === "fetching") {
      return false;
    }

    if (FindColumn === undefined) {
      return false;
    }

    if (FindText === ReplaceText) {
      return false;
    }

    if (!validFindText) {
      return false;
    }

    if (!validReplaceText) {
      return false;
    }

    if (FindText.length === 0 && FindType !== "Equals") {
      return false;
    }

    return true;
  },
});

export const modalBulkFindReplaceCanReplaceState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceCanReplaceState",
  get: ({ get }) => {
    const { totalItems, previewStatus } = get(modalBulkFindReplaceState);
    const canPreview = get(modalBulkFindReplaceCanPreviewState);

    return canPreview && previewStatus === "ready" && totalItems > 0;
  },
});

export const modalBulkFindReplacePreviewResultsState = selector<
  ModalBulkFindReplaceState["items"]
>({
  key: "recipientsList_modalBulkFindReplacePreviewResultsState",
  get: ({ get }) => {
    const { pageNumber, pageSize, items } = get(modalBulkFindReplaceState);

    return items.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  },
});

export const modalBulkFindReplaceHasNoResultsState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceHasNoResultsState",
  get: ({ get }) => {
    const {
      previewStatus,
      totalItems,
      FindColumn,
      FindText,
      FindType,
      ReplaceText,
    } = get(modalBulkFindReplaceState);

    return (
      (previewStatus === "ready" && totalItems === 0) ||
      (FindColumn !== undefined &&
        FindText.length === 0 &&
        ReplaceText.length > 0 &&
        FindType !== "Equals")
    );
  },
});

export const modalBulkFindReplaceHasResultsState = selector<boolean>({
  key: "recipientsList_modalBulkFindReplaceHasResultsState",
  get: ({ get }) => {
    const hasNoResults = get(modalBulkFindReplaceHasNoResultsState);
    const { previewStatus, totalItems } = get(modalBulkFindReplaceState);

    return (
      !hasNoResults &&
      (previewStatus === "fetching" ||
        previewStatus === "paging" ||
        (previewStatus === "ready" && totalItems > 0))
    );
  },
});
